import React, { useContext, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { NavbarContext } from "../../context";
import { automationWork, caseStudies, moreWork } from "../../data/portfolioData";
import { N8nSimulator } from "../../components/N8nSimulator";

const ExternalIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3M9 2h5v5M14 2 8 8"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const GithubIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z"/>
  </svg>
);

const CaseStudy = ({ data, index }) => {
  const { ref, inView } = useInView({ threshold: 0.08, triggerOnce: true });
  const headingId = `case-study-${index}`;

  return (
    <article
      ref={ref}
      className={`case-study fade-up${inView ? " visible" : ""}`}
      aria-labelledby={headingId}
    >
      <div className="case-head">
        <div className="case-intro">
          <p className="case-meta">
            <span>{data.kind}</span>
            <span>{data.period}</span>
          </p>
          <h3 id={headingId} className="case-title">{data.title}</h3>
          <p className="case-role">{data.role}</p>
          <p className="case-problem">{data.problem}</p>
          {data.liveLink && (
            <a
              href={data.liveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link primary-link case-link"
            >
              <ExternalIcon /> Visit live site
            </a>
          )}
        </div>
        <div className="project-image-wrapper case-image">
          <img
            src={data.image}
            alt={data.imageAlt}
            className="project-image"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      <div className="case-body">
        <div className="case-col">
          <h4 className="case-col-title">How I ran it</h4>
          <ul className="case-list">
            {data.delivery.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <div className="case-col">
          <h4 className="case-col-title">What shipped</h4>
          <ul className="case-list">
            {data.build.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </div>

      <div className="case-foot">
        <p className="case-outcome">
          <span>Business result</span>
          <span className="marker-text">{data.outcome}</span>
        </p>
        <div className="project-stack" aria-label={`${data.title} tech stack`}>
          {data.stack.map((tech) => (
            <span key={tech} className="project-tech-tag">{tech}</span>
          ))}
        </div>
      </div>
    </article>
  );
};

export const Projects = () => {
  const { ref, inView } = useInView({ threshold: 0.1 });
  const setPage = useContext(NavbarContext);

  useEffect(() => {
    if (inView) setPage("projects");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <section
      ref={ref}
      id="projects-page"
      className="projects-section"
      aria-labelledby="projects-heading"
    >
      <div className="container-wide">
        <h2 id="projects-heading" className="section-heading">
          <mark className="marker">Results</mark> for real businesses.
        </h2>
        <p className="section-sub">
          The business problem, how I ran the work, what shipped, and what changed for the owner.
        </p>

        <div className="case-studies">
          {caseStudies.map((item, i) => (
            <CaseStudy key={item.title} data={item} index={i} />
          ))}
        </div>

        <div className="more-work">
          <h3 className="more-work-heading">More shipped work</h3>
          <ul className="more-work-list">
            {moreWork.map((item) => (
              <li key={item.title} className="more-work-item">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="more-work-thumb"
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <p className="more-work-top">
                    <span className="project-name">{item.title}</span>
                    <span className="project-period">{item.period}</span>
                  </p>
                  <p className="more-work-role">{item.role}</p>
                  <p className="project-desc">{item.summary}</p>
                  <div className="project-links-inline">
                    {item.liveLink && (
                      <a
                        href={item.liveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link primary-link"
                        aria-label={`Visit ${item.title}`}
                      >
                        <ExternalIcon /> Live
                      </a>
                    )}
                    {item.codeLink && (
                      <a
                        href={item.codeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                        aria-label={`${item.title} source code`}
                      >
                        <GithubIcon /> Code
                      </a>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <article className="automation-row" aria-labelledby="automation-heading">
          <div>
            <h3 id="automation-heading" className="more-work-heading">{automationWork.title}</h3>
            <p className="more-work-role">{automationWork.role}, {automationWork.period}</p>
            <p className="project-desc">{automationWork.summary}</p>
            <div className="project-stack">
              {automationWork.stack.map((tech) => (
                <span key={tech} className="project-tech-tag">{tech}</span>
              ))}
            </div>
          </div>
          <N8nSimulator />
        </article>
      </div>
    </section>
  );
};
